/*
Copyright (C) 2024 Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import { onSwiftRefresh } from "@src/components/modules/TemplateModule/TopBar/refresh";
import { useNavigate } from "react-router";

import MainTemplate from "@src/components/modules/TemplateModule/MainTemplate";
import Navigation from "@src/components/modules/NavigationModule/Navigation";
import FilterList from "@src/components/ui/Lists/FilterList";
import PageHeader from "@src/components/smart/PageHeader";
import AlertModal from "@src/components/ui/AlertModal";

import projectStore from "@src/stores/ProjectStore";
import deploymentStore from "@src/stores/DeploymentStore";
import endpointStore from "@src/stores/EndpointStore";
import notificationStore from "@src/stores/NotificationStore";
import configLoader from "@src/utils/Config";
import RequestCutoverDialog from "@src/components/modules/CutoverModule/RequestCutoverDialog";
import { RollbackDialog } from "@src/components/modules/CutoverModule/Rollback";
import type { RollbackDeployment } from "@src/components/modules/CutoverModule/Rollback";
import instanceStore from "@src/stores/InstanceStore";
import minionPoolStore from "@src/stores/MinionPoolStore";
import transferSource from "@src/sources/TransferSource";
import type { TransferItemDetails } from "@src/@types/MainItem";

import { ThemePalette } from "@src/components/Theme";
import deploymentFields from "@src/components/modules/TransferModule/DeploymentOptions/DeploymentFields";
import { DeploymentItem } from "@src/@types/MainItem";
import userStore from "@src/stores/UserStore";
import TransferListItem from "@src/components/modules/TransferModule/TransferListItem";
import {
  DEPLOYMENT_COLUMNS,
  transferScenario,
} from "@src/components/modules/TransferModule/TransferListItem/TransferListItem";
import { providerLabel } from "@src/components/modules/EndpointModule/EndpointLogos/ProviderMark";
import {
  ClearFiltersButton,
  FilterSelect,
  Segmented,
  ToolbarDateRange,
  ToolbarSearch,
  matchesSearch,
  useListQuery,
  withinDays,
} from "@src/components/ui/Lists/ListToolbar";
import type { ListQuery } from "@src/components/ui/Lists/ListToolbar";
import { parseApiDate } from "@src/components/ui/Lists/ListTable";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";
import { Rocket } from "lucide-react";

const Wrapper = styled.div<any>``;

type State = {
  selectedDeployments: DeploymentItem[];
  modalIsOpen: boolean;
  showDeleteDeploymentModal: boolean;
  showCancelDeploymentModal: boolean;
  showRecreateDeploymentsModal: boolean;
  /** Swift: rollback and "request a new cut-over" from the row menu. */
  rollbackDeployment: DeploymentItem | null;
  cutoverTransfer: TransferItemDetails | null;
};

type Props = {
  onNavigate: (path: string) => void;
  query: ListQuery;
};

const STATUS_GROUPS: { [group: string]: string[] } = {
  running: ["RUNNING", "PENDING", "STARTING", "AWAITING_MINION_ALLOCATIONS", "CANCELLING", "CANCELLING_AFTER_COMPLETION"],
  failed: ["ERROR", "FAILED_TO_SCHEDULE", "FAILED_TO_CANCEL", "ERROR_ALLOCATING_MINIONS", "DEADLOCKED", "STRANDED_AFTER_DEADLOCK"],
  completed: ["COMPLETED"],
  cancelled: ["CANCELED", "FORCE_CANCELED", "CANCELED_AFTER_COMPLETION", "CANCELED_FOR_DEBUGGING"],
};
const FILTER_KEYS = ["q", "status", "type", "source", "target", "from", "to"];

@observer
class DeploymentsPage extends React.Component<Props, State> {
  state: State = {
    showDeleteDeploymentModal: false,
    showCancelDeploymentModal: false,
    showRecreateDeploymentsModal: false,
    rollbackDeployment: null,
    cutoverTransfer: null,
    selectedDeployments: [],
    modalIsOpen: false,
  };

  pollTimeout = 0;

  stopPolling = false;

  offSwiftRefresh?: () => void;

  componentDidMount() {
    // The top bar's Refresh reloads this page's data.
    this.offSwiftRefresh = onSwiftRefresh(() => {
      this.handleReloadButtonClick();
    });
    document.title = "Deployments · SwiftIQ";

    deploymentStore.resetDeploymentPagination();

    projectStore.getProjects();
    endpointStore.getEndpoints({ showLoading: true });
    userStore.getAllUsers({
      showLoading: userStore.users.length === 0,
      quietError: true,
    });

    this.stopPolling = false;
    this.pollData();
  }

  componentWillUnmount() {
    this.offSwiftRefresh?.();
    clearTimeout(this.pollTimeout);
    this.stopPolling = true;
  }

  getEndpoint(endpointId: string) {
    return endpointStore.endpoints.find(endpoint => endpoint.id === endpointId);
  }

  get filtersActive() {
    return FILTER_KEYS.some(k => this.props.query.get(k));
  }

  clearFilters() {
    const changes: { [k: string]: null } = {};
    FILTER_KEYS.forEach(k => {
      changes[k] = null;
    });
    this.props.query.set(changes);
  }

  endpointName(id: string) {
    return this.getEndpoint(id)?.name;
  }

  filteredDeployments(except?: string): DeploymentItem[] {
    const q = this.props.query;
    const text = q.get("q");
    const status = q.get("status");
    const type = q.get("type");
    const sources = q.getList("source");
    const targets = q.getList("target");
    const from = q.get("from");
    const to = q.get("to");
    return deploymentStore.deployments.filter(item => {
      if (except !== "status" && status && !(STATUS_GROUPS[status] || []).includes(item.last_execution_status)) return false;
      if (type) {
        const isMigration = transferScenario(item) === "live_migration";
        if ((type === "migration") !== isMigration) return false;
      }
      if (sources.length && !sources.includes(this.getEndpoint(item.origin_endpoint_id)?.type || "")) return false;
      if (targets.length && !targets.includes(this.getEndpoint(item.destination_endpoint_id)?.type || "")) return false;
      if (!withinDays(parseApiDate(item.created_at), from, to)) return false;
      if (text) {
        return matchesSearch(text, [
          item.id,
          item.notes,
          item.description,
          ...item.instances,
          this.endpointName(item.origin_endpoint_id),
          this.endpointName(item.destination_endpoint_id),
          userStore.users.find(u => u.id === item.user_id)?.name,
        ]);
      }
      return true;
    });
  }

  renderToolbar() {
    const q = this.props.query;
    const forStatus = this.filteredDeployments("status");
    const count = (group: string) =>
      forStatus.filter(d => STATUS_GROUPS[group].includes(d.last_execution_status)).length;
    const providerOptions = (key: "origin_endpoint_id" | "destination_endpoint_id") => {
      const types = new Set<string>();
      deploymentStore.deployments.forEach(d => {
        const t = this.getEndpoint(d[key])?.type;
        if (t) types.add(t);
      });
      return [...types]
        .map(t => ({ value: t, label: providerLabel(t) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    };
    return (
      <>
        <ToolbarSearch
          value={q.get("q")}
          placeholder="Search name, VM or endpoint"
          onChange={v => q.set({ q: v })}
        />
        <Segmented
          label="Status"
          value={q.get("status")}
          onChange={v => q.set({ status: v })}
          options={[
            { value: "", label: "All" },
            { value: "running", label: "Running", count: count("running") },
            { value: "failed", label: "Failed", count: count("failed") },
            { value: "completed", label: "Completed", count: count("completed") },
            { value: "cancelled", label: "Cancelled", count: count("cancelled") },
          ]}
        />
        <FilterSelect
          label="Type"
          value={q.get("type")}
          onChange={v => q.set({ type: v })}
          options={[
            { value: "replica", label: "From a replica" },
            { value: "migration", label: "Migration" },
          ]}
        />
        <FilterSelect
          label="Source"
          multiple
          value={q.getList("source")}
          onChange={v => q.set({ source: v })}
          options={providerOptions("origin_endpoint_id")}
        />
        <FilterSelect
          label="Target"
          multiple
          value={q.getList("target")}
          onChange={v => q.set({ target: v })}
          options={providerOptions("destination_endpoint_id")}
        />
        <ToolbarDateRange
          label="Created"
          from={q.get("from")}
          to={q.get("to")}
          onChange={r => q.set({ from: r.from, to: r.to })}
        />
        {this.filtersActive ? <ClearFiltersButton onClick={() => this.clearFilters()} /> : null}
      </>
    );
  }

  rowActions(item: DeploymentItem): DropdownAction[] {
    const status = item.last_execution_status;
    const running = STATUS_GROUPS.running.includes(status);
    return [
      { label: "Open", action: () => this.props.onNavigate(`/deployments/${item.id}`) },
      { label: "Tasks", action: () => this.props.onNavigate(`/deployments/${item.id}/tasks`) },
      {
        label: "Open transfer",
        hidden: !item.transfer_id,
        action: () => this.props.onNavigate(`/transfers/${item.transfer_id}`),
      },
      {
        label: "Cancel",
        hidden: !running,
        action: () => this.setState({ selectedDeployments: [item], showCancelDeploymentModal: true }),
      },
      {
        label: "Roll back…",
        hidden: !(item as RollbackDeployment).rollback_allowed,
        action: () => this.setState({ rollbackDeployment: item }),
      },
      this.cutoverApprovals && item.transfer_id
        ? {
            label: "Request a new cut-over…",
            disabled: running,
            action: () => this.openCutover(item),
          }
        : {
            label: "Recreate…",
            disabled: running,
            action: () => this.setState({ selectedDeployments: [item], showRecreateDeploymentsModal: true }),
          },
      {
        label: "Delete…",
        color: ThemePalette.alert,
        action: () => this.setState({ selectedDeployments: [item], showDeleteDeploymentModal: true }),
      },
    ];
  }

  get cutoverApprovals() {
    return configLoader.config?.cutoverApprovals !== false;
  }

  async openCutover(item: DeploymentItem) {
    try {
      const transfer = await transferSource.getTransferDetails({ transferId: item.transfer_id });
      instanceStore.loadInstancesDetails({
        endpointId: transfer.origin_endpoint_id,
        instances: transfer.instances.map(id => ({ id })),
        env: transfer.source_environment,
        quietError: true,
      });
      minionPoolStore.loadMinionPools();
      this.setState({ cutoverTransfer: transfer });
    } catch (err) {
      notificationStore.alert("Could not load the deployment's transfer.", "error");
    }
  }

  getStatus(deploymentId: string): string {
    const deployment = deploymentStore.deployments.find(
      m => m.id === deploymentId,
    );
    return deployment ? deployment.last_execution_status : "";
  }

  getDeploymentType(deploymentId: string): string {
    const deployment = deploymentStore.deployments.find(
      m => m.id === deploymentId,
    );
    return deployment ? deployment.transfer_scenario_type : "";
  }

  handleProjectChange() {
    deploymentStore.resetDeploymentPagination();
    endpointStore.getEndpoints({ showLoading: true });
    deploymentStore.getDeployments({ showLoading: true });
  }

  handleReloadButtonClick() {
    projectStore.getProjects();
    endpointStore.getEndpoints({ showLoading: true });
    deploymentStore.getDeployments({ showLoading: true });
    userStore.getAllUsers({ showLoading: true, quietError: true });
  }

  handleItemClick(item: DeploymentItem) {
    if (item.last_execution_status === "RUNNING") {
      this.props.onNavigate(`/deployments/${item.id}/tasks`);
    } else {
      this.props.onNavigate(`/deployments/${item.id}`);
    }
  }

  deleteSelectedDeployments() {
    this.state.selectedDeployments.forEach(deployment => {
      deploymentStore.delete(deployment.id);
    });
    this.setState({ showDeleteDeploymentModal: false });
  }

  cancelSelectedDeployments() {
    this.state.selectedDeployments.forEach(deployment => {
      const status = this.getStatus(deployment.id);
      if (status === "RUNNING" || status === "AWAITING_MINION_ALLOCATIONS") {
        deploymentStore.cancel(deployment.id);
      }
    });
    notificationStore.alert("Cancelling deployments");
    this.setState({ showCancelDeploymentModal: false });
  }

  async recreateDeployments() {
    notificationStore.alert("Recreating deployments");
    this.setState({ showRecreateDeploymentsModal: false });

    await Promise.all(
      this.state.selectedDeployments.map(async deployment => {
        if (deployment.transfer_id) {
          await deploymentStore.deployTransfer({
            transferId: deployment.transfer_id,
            fields: deploymentFields,
            uploadedUserScripts: [],
            removedUserScripts: [],
            userScriptData: deployment.user_scripts,
            minionPoolMappings:
              deployment.instance_osmorphing_minion_pool_mappings || {},
          });
        } else {
          await deploymentStore.recreateFullCopy(deployment as any);
        }
      }),
    );

    deploymentStore.getDeployments();
  }

  handleEmptyListButtonClick() {
    this.props.onNavigate("/wizard/migration");
  }

  handleModalOpen() {
    this.setState({ modalIsOpen: true });
  }

  handleModalClose() {
    this.setState({ modalIsOpen: false }, () => {
      this.pollData();
    });
  }

  async pollData() {
    if (this.state.modalIsOpen || this.stopPolling) {
      return;
    }

    await Promise.all([
      deploymentStore.getDeployments({ skipLog: true }),
      endpointStore.getEndpoints({ skipLog: true }),
      userStore.getAllUsers({ skipLog: true, quietError: true }),
    ]);
    this.pollTimeout = window.setTimeout(() => {
      this.pollData();
    }, configLoader.config.requestPollTimeout);
  }

  render() {
    let atLeaseOneIsRunning = false;
    this.state.selectedDeployments.forEach(deployment => {
      const status = this.getStatus(deployment.id);
      atLeaseOneIsRunning =
        atLeaseOneIsRunning ||
        status === "RUNNING" ||
        status === "AWAITING_MINION_ALLOCATIONS";
    });
    const BulkActions = [
      {
        label: "Cancel",
        disabled: !atLeaseOneIsRunning,
        action: () => {
          this.setState({ showCancelDeploymentModal: true });
        },
      },
      {
        label: "Recreate",
        disabled: atLeaseOneIsRunning,
        color: ThemePalette.primary,
        action: () => {
          this.setState({ showRecreateDeploymentsModal: true });
        },
      },
      {
        label: "Delete",
        color: ThemePalette.alert,
        action: () => {
          this.setState({ showDeleteDeploymentModal: true });
        },
      },
    ];

    return (
      <Wrapper>
        <MainTemplate
          navigationComponent={<Navigation currentPage="deployments" />}
          listComponent={
            <FilterList
              toolbar={this.renderToolbar()}
              filtersActive={this.filtersActive}
              onClearFilters={() => this.clearFilters()}
              columns={DEPLOYMENT_COLUMNS}
              selectionLabel="deployment"
              loading={deploymentStore.loading}
              items={this.filteredDeployments()}
              onItemClick={item => {
                this.handleItemClick(item);
              }}
              onReloadButtonClick={() => {
                this.handleReloadButtonClick();
              }}
              onSelectedItemsChange={selectedDeployments => {
                this.setState({ selectedDeployments });
              }}
              dropdownActions={BulkActions}
              apiPagination={{
                currentPage: deploymentStore.deploymentsPage,
                hasNextPage: deploymentStore.deploymentsHasNextPage,
                itemsPerPage: deploymentStore.deploymentsItemsPerPage,
                onPageChange: page => {
                  deploymentStore.setDeploymentsPage(page);
                },
                onItemsPerPageChange: e => {
                  deploymentStore.setDeploymentsItemsPerPage(
                    parseInt(e.target.value, 10),
                  );
                },
              }}
              renderItemComponent={options => (
                <TransferListItem
                  {...options}
                  endpointType={id => {
                    const endpoint = this.getEndpoint(id);
                    if (endpoint) {
                      return endpoint.type;
                    }
                    if (endpointStore.loading) {
                      return "Loading...";
                    }
                    return "Not Found";
                  }}
                  getEndpointName={id => this.endpointName(id)}
                  getUserName={id =>
                    userStore.users.find(u => u.id === id)?.name
                  }
                  userNameLoading={userStore.allUsersLoading}
                  actions={this.rowActions(options.item)}
                />
              )}
              emptyListIcon={Rocket}
              emptyListMessage="No deployments in this project yet"
              emptyListExtraMessage="A deployment brings up a transfer's virtual machines on the target cloud. Deploy one from the Transfers list."
              emptyListButtonLabel="New migration"
              onEmptyListButtonClick={() => {
                this.handleEmptyListButtonClick();
              }}
            />
          }
          headerComponent={
            <PageHeader
              title="Deployments"
              subtitle="Every cut-over from a transfer to its target."
              onProjectChange={() => {
                this.handleProjectChange();
              }}
              onModalOpen={() => {
                this.handleModalOpen();
              }}
              onModalClose={() => {
                this.handleModalClose();
              }}
            />
          }
        />
        {this.state.showDeleteDeploymentModal ? (
          <AlertModal
            isOpen
            title="Delete selected deployments?"
            message="Are you sure you want to delete the selected deployments?"
            extraMessage="Deleting a SwiftIQ deployment is permanent!"
            onConfirmation={() => {
              this.deleteSelectedDeployments();
            }}
            onRequestClose={() => {
              this.setState({ showDeleteDeploymentModal: false });
            }}
          />
        ) : null}
        {this.state.showCancelDeploymentModal ? (
          <AlertModal
            isOpen
            title="Cancel selected deployments?"
            message="Are you sure you want to cancel the selected deployments?"
            extraMessage="Cancelling a SwiftIQ deployment is permanent!"
            onConfirmation={() => {
              this.cancelSelectedDeployments();
            }}
            onRequestClose={() => {
              this.setState({ showCancelDeploymentModal: false });
            }}
          />
        ) : null}
        {this.state.showRecreateDeploymentsModal ? (
          <AlertModal
            isOpen
            title="Recreate selected deployments?"
            message="Are you sure you want to recreate the selected deployments?"
            extraMessage="Deployments will be recreated using current transfer source and destination options."
            onConfirmation={() => {
              this.recreateDeployments();
            }}
            onRequestClose={() => {
              this.setState({ showRecreateDeploymentsModal: false });
            }}
          />
        ) : null}
        {this.state.rollbackDeployment ? (
          <RollbackDialog
            deployment={this.state.rollbackDeployment as RollbackDeployment}
            sourceType={this.getEndpoint(this.state.rollbackDeployment.origin_endpoint_id)?.type}
            targetType={this.getEndpoint(this.state.rollbackDeployment.destination_endpoint_id)?.type}
            onClose={() => this.setState({ rollbackDeployment: null })}
            onStarted={() => {
              deploymentStore.getDeployments({ showLoading: false });
            }}
          />
        ) : null}
        {this.state.cutoverTransfer ? (
          <RequestCutoverDialog
            transfer={this.state.cutoverTransfer}
            instancesDetails={instanceStore.instancesDetails}
            loadingInstances={instanceStore.loadingInstancesDetails}
            minionPools={minionPoolStore.minionPools.filter(
              m =>
                m.endpoint_id === this.state.cutoverTransfer?.destination_endpoint_id &&
                m.platform === "destination",
            )}
            onClose={() => this.setState({ cutoverTransfer: null })}
            onCreated={request => this.props.onNavigate(`/cutovers/${request.id}`)}
          />
        ) : null}
      </Wrapper>
    );
  }
}

function DeploymentsPageWithNavigate() {
  const navigate = useNavigate();
  const query = useListQuery();

  return <DeploymentsPage onNavigate={navigate} query={query} />;
}

export default DeploymentsPageWithNavigate;
